
import { useState } from "react";
import { useNotify } from "@/hooks/useNotify";
import { supabase } from "@/integrations/supabase/client";
import { retryWithBackoff } from "../utils/retryUtils";
import { JoinStep } from "../types";

export const usePasswordCreation = (
  invitationId: string | null,
  token: string | null,
  invitationEmail: string,
  setCurrentStep: (step: JoinStep) => void
) => {
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();

  const handleCreatePassword = async (password: string, confirmPassword: string) => {
    if (!invitationId) return;
    
    try {
      setIsLoading(true);

      if (password !== confirmPassword) {
        notify.error("Please make sure your passwords match");
        return;
      }

      console.log("Creating new account for:", invitationEmail);

      // Create account with retry logic
      const createResult = await retryWithBackoff(async () => {
        const { data, error } = await supabase.functions.invoke('create-invited-user', {
          body: {
            email: invitationEmail,
            password: password,
            invitationId: invitationId,
            token: token
          }
        });

        if (error) {
          console.error("Invitation signup error:", error);
          throw new Error(error.message || "Failed to create account");
        }

        if (!data.success) {
          console.error("User creation failed:", data);
          throw new Error(data.error || "Failed to create account");
        }

        return data;
      }, 3, 2000);

      console.log("Account created successfully");

      // Direct session verification approach
      let sessionEstablished = false;
      let attempts = 0;
      const maxAttempts = 3;

      while (!sessionEstablished && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          console.log(`Session established successfully on attempt ${attempts + 1}`);
          sessionEstablished = true;
          setCurrentStep(JoinStep.PROFILE_INFO);
          notify.success("Please complete your profile information.");
          break;
        }
        
        attempts++;
        console.log(`Session verification attempt ${attempts} - no valid session`);
      }

      if (!sessionEstablished) {
        // Fallback: manual sign-in
        console.log("Auto-session failed, attempting manual sign-in");
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: invitationEmail,
          password: password,
        });

        if (signInError) {
          console.error("Manual sign-in failed:", signInError);
          throw new Error("Authentication failed. Please refresh and try again.");
        }

        if (signInData.session) {
          console.log("Manual sign-in successful");
          // Allow time for auth context to update
          setTimeout(() => {
            setCurrentStep(JoinStep.PROFILE_INFO);
            notify.success("Please complete your profile information.");
          }, 1000);
        } else {
          throw new Error("Failed to establish authentication session");
        }
      }

    } catch (error: any) {
      console.error("Error creating account:", error);
      notify.error(error.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleCreatePassword
  };
};
